import { computed, inject, markRaw, provide, reactive, ref, shallowRef } from "vue";
import * as Cesium from "cesium";
import {
  LAYER_DEFS,
  LAYER_DATA_VERSION,
  loadLayerByKey,
  loadWaterLayers,
  itemToMapDegrees,
  loadApiJson,
  applyPolderDisplayMode,
  applyPolderCameraLod,
} from "@/cesium/loadWaterData";
import { PAGE_BOOT_KEYS, PAGE_LAYER_KEYS } from "@/cesium/pageLayers";
import { destroySharedViewer, flyToLonLat } from "@/cesium/createViewer";
import { resolvePointIconUrl } from "@/cesium/pointIcons";

export const MAP_KEY = Symbol("wt-map");

const defMap = Object.fromEntries(LAYER_DEFS.map((d) => [d.key, d]));

export function createMapStore() {
  const viewer = shallowRef(null);
  const layers = ref([]); // { key, name, group, colorHex, visible, dataSource, loading? }
  const picked = ref(null);
  /** 圩区悬停名称提示 { text, x, y } */
  const hoverTip = ref(null);
  const activePage = ref("home");
  const booting = ref(false);

  let pickHandler = null;
  let removePolderLod = null;

  const layerByKey = computed(() => {
    const m = new Map();
    layers.value.forEach((l) => m.set(l.key, l));
    return m;
  });

  function syncPolderLod() {
    const v = viewer.value;
    const polder = layerByKey.value.get("polder");
    if (!v || v.isDestroyed?.() || !polder?.dataSource) return;
    const h = v.camera.positionCartographic.height;
    // 总览 / 水雨情：近景隐藏圩区，只留点位；其它页始终显示
    const enabled =
      activePage.value === "hydro" || activePage.value === "home";
    applyPolderCameraLod(polder.dataSource, h, enabled);
  }

  function pageDefs(pageId = activePage.value) {
    const keys = PAGE_LAYER_KEYS[pageId] || [];
    return keys.map((k) => defMap[k]).filter(Boolean);
  }

  function pageLayerItems(pageId = activePage.value) {
    const loaded = layerByKey.value;
    return pageDefs(pageId).map((def) => {
      const cur = loaded.get(def.key);
      return {
        key: def.key,
        name: def.name,
        group: def.group,
        colorHex: def.colorHex,
        diamLabel: def.diamLabel,
        iconUrl: resolvePointIconUrl(def),
        visible: cur ? !!cur.visible : false,
        loading: !!cur?.loading,
        loaded: !!cur?.dataSource,
        dataSource: cur?.dataSource,
      };
    });
  }

  function syncVisibilityForPage(pageId) {
    const allow = new Set(PAGE_LAYER_KEYS[pageId] || []);
    layers.value.forEach((l) => {
      if (!l.dataSource) return;
      const show = allow.has(l.key) && l.visible;
      l.dataSource.show = show;
      if (l.key === "polder") {
        applyPolderDisplayMode(
          l.dataSource,
          pageId === "hydro" ? "outline" : "fill"
        );
      }
    });
    syncPolderLod();
  }

  function bindPick(v) {
    pickHandler?.destroy?.();
    pickHandler = new Cesium.ScreenSpaceEventHandler(v.scene.canvas);
    let hoveredEntity = null;

    function clearHover() {
      if (!hoveredEntity) {
        hoverTip.value = null;
        return;
      }
      if (hoveredEntity._xcPolder && hoveredEntity.polygon) {
        if (hoveredEntity._xcPolderOutline) {
          hoveredEntity.polygon.material = Cesium.Color.WHITE.withAlpha(0.02);
          if (hoveredEntity.polyline && hoveredEntity._xcStrokeBase) {
            hoveredEntity.polyline.width = 2;
            hoveredEntity.polyline.material = new Cesium.PolylineDashMaterialProperty({
              color: hoveredEntity._xcStrokeBase,
              dashLength: 16,
            });
          }
        } else if (hoveredEntity._xcFillBase) {
          hoveredEntity.polygon.material = hoveredEntity._xcFillBase.clone();
        }
      }
      if (hoveredEntity._xcDike && hoveredEntity.polyline && hoveredEntity._xcStrokeBase) {
        hoveredEntity.polyline.material = hoveredEntity._xcStrokeBase.clone();
        hoveredEntity.polyline.width = 4;
      }
      hoveredEntity = null;
      hoverTip.value = null;
    }

    function updatePolderTip(entity) {
      const center = entity._xcCenter;
      const text = entity._xcPolderName || entity.name || "圩区";
      if (!center) {
        hoverTip.value = null;
        return;
      }
      const win =
        Cesium.SceneTransforms.worldToWindowCoordinates(v.scene, center) ||
        v.scene.cartesianToCanvasCoordinates(center);
      if (!Cesium.defined(win)) {
        hoverTip.value = null;
        return;
      }
      const rect = v.scene.canvas.getBoundingClientRect();
      hoverTip.value = {
        text,
        x: rect.left + win.x,
        y: rect.top + win.y,
      };
    }

    function setHover(entity) {
      if (hoveredEntity === entity) {
        if (entity?._xcPolder) updatePolderTip(entity);
        return;
      }
      clearHover();
      if (!entity) return;

      if (entity._xcPolder && entity.polygon) {
        if (entity._xcPolderOutline) {
          // 悬停：透明浅蓝填充
          entity.polygon.material = Cesium.Color.fromCssColorString(
            "rgba(61, 231, 255, 0.22)"
          );
          if (entity.polyline && entity._xcStrokeBase) {
            entity.polyline.width = 2;
            entity.polyline.material = new Cesium.PolylineDashMaterialProperty({
              color: entity._xcStrokeBase.withAlpha(1),
              dashLength: 16,
            });
          }
        } else if (entity._xcFillBase) {
          const alpha = entity._xcFillHoverAlpha ?? 0.85;
          entity.polygon.material = entity._xcFillBase.withAlpha(alpha);
        }
        hoveredEntity = entity;
        updatePolderTip(entity);
        return;
      }
      if (entity._xcDike && entity.polyline && entity._xcStrokeBase) {
        const alpha = entity._xcStrokeHoverAlpha ?? 1;
        entity.polyline.material = entity._xcStrokeBase.withAlpha(alpha);
        entity.polyline.width = 5;
        hoveredEntity = entity;
      }
    }

    pickHandler.setInputAction((movement) => {
      const drilled = v.scene.drillPick(movement.endPosition, 8);
      let entity = null;
      for (const obj of drilled) {
        if (
          Cesium.defined(obj?.id) &&
          obj.id instanceof Cesium.Entity &&
          (obj.id._xcPolder || obj.id._xcDike)
        ) {
          entity = obj.id;
          break;
        }
      }
      if (!entity) {
        const one = v.scene.pick(movement.endPosition);
        if (
          Cesium.defined(one?.id) &&
          one.id instanceof Cesium.Entity &&
          (one.id._xcPolder || one.id._xcDike)
        ) {
          entity = one.id;
        }
      }
      if (entity) {
        setHover(entity);
        v.scene.canvas.style.cursor = "pointer";
      } else {
        clearHover();
        v.scene.canvas.style.cursor = "";
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    pickHandler.setInputAction((movement) => {
      // drillPick：标签盖住图标时仍能点到下方实体
      const drilled = v.scene.drillPick(movement.position, 8);
      let entity = null;
      for (const obj of drilled) {
        if (Cesium.defined(obj?.id) && obj.id instanceof Cesium.Entity) {
          entity = obj.id;
          break;
        }
      }
      if (!entity) {
        const one = v.scene.pick(movement.position);
        if (Cesium.defined(one?.id) && one.id instanceof Cesium.Entity) {
          entity = one.id;
        }
      }
      if (!entity) {
        picked.value = null;
        return;
      }

      const time = v.clock.currentTime;
      let cartesian = null;
      try {
        cartesian = entity.position?.getValue?.(time) || null;
      } catch (_) {
        cartesian = null;
      }
      // 面/线无单点 position 时，用点击处椭球交点
      if (!cartesian) {
        const ray = v.camera.getPickRay(movement.position);
        cartesian = ray ? v.scene.globe.pick(ray, v.scene) : null;
      }

      const fields = fieldsFromEntity(entity) || {
        名称: entity.name || "未命名要素",
      };

      picked.value = {
        fields,
        kind: entity._xcPolder ? "polder" : undefined,
        cartesian: cartesian ? markRaw(cartesian) : null,
      };
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  function setViewer(v) {
    viewer.value = markRaw(v);
    // 半透明圩区面可被拾取
    v.scene.pickTranslucentDepth = true;
    bindPick(v);
    bindPolderLod(v);
  }

  function bindPolderLod(v) {
    removePolderLod?.();
    removePolderLod = null;
    if (!v || v.isDestroyed?.()) return;

    const onChanged = () => syncPolderLod();
    v.camera.changed.addEventListener(onChanged);
    // 更灵敏地响应缩放
    if (v.camera.percentageChanged > 0.12) {
      v.camera.percentageChanged = 0.08;
    }
    removePolderLod = () => {
      try {
        if (!v.isDestroyed?.()) {
          v.camera.changed.removeEventListener(onChanged);
        }
      } catch (_) {
        /* ignore */
      }
    };
    syncPolderLod();
  }

  async function ensureLayers(keys, onProgress) {
    const v = viewer.value;
    if (!v || !keys?.length) return [];

    // 丢弃旧版坐标转换缓存（如未 GCJ→WGS 的圩区）
    const stale = layers.value.filter(
      (l) =>
        keys.includes(l.key) &&
        l.dataSource &&
        l.dataVersion !== LAYER_DATA_VERSION
    );
    for (const l of stale) {
      try {
        if (l.dataSource?.clustering) l.dataSource.clustering.enabled = false;
        v.dataSources.remove(l.dataSource, true);
      } catch (_) {
        /* ignore */
      }
      const i = layers.value.findIndex((x) => x.key === l.key);
      if (i >= 0) layers.value.splice(i, 1);
      console.log(`[xc] 丢弃旧图层缓存: ${l.key} (v${l.dataVersion}→v${LAYER_DATA_VERSION})`);
    }

    const missing = keys.filter((k) => !layerByKey.value.get(k)?.dataSource);
    const existed = [];

    for (const k of keys) {
      const cur = layerByKey.value.get(k);
      if (cur?.dataSource) {
        cur.visible = true;
        existed.push(cur);
      }
    }

    if (!missing.length) {
      syncVisibilityForPage(activePage.value);
      return existed;
    }

    // mark loading
    missing.forEach((k) => {
      const def = defMap[k];
      if (!def) return;
      const idx = layers.value.findIndex((l) => l.key === k);
      const stub = {
        key: k,
        name: def.name,
        group: def.group,
        colorHex: def.colorHex,
        visible: false,
        loading: true,
        dataSource: null,
        dataVersion: LAYER_DATA_VERSION,
      };
      if (idx >= 0) layers.value[idx] = { ...layers.value[idx], ...stub };
      else layers.value.push(stub);
    });

    const loaded = await loadWaterLayers(v, missing, onProgress);
    loaded.forEach((item) => {
      const i = layers.value.findIndex((l) => l.key === item.key);
      const row = {
        ...item,
        loading: false,
        visible: true,
        dataVersion: LAYER_DATA_VERSION,
      };
      if (i >= 0) layers.value[i] = row;
      else layers.value.push(row);
    });

    // clear failed loading stubs
    layers.value = layers.value.filter((l) => l.dataSource || !l.loading);
    syncVisibilityForPage(activePage.value);
    return loaded;
  }

  async function activatePage(pageId, onProgress) {
    activePage.value = pageId;
    picked.value = null;
    booting.value = true;
    try {
      const boot = PAGE_BOOT_KEYS[pageId] || [];
      await ensureLayers(boot, onProgress);
      // 非 boot 但已加载的：保持 visible 标志，但仅白名单内显示
      layers.value.forEach((l) => {
        const allow = (PAGE_LAYER_KEYS[pageId] || []).includes(l.key);
        if (!allow && l.dataSource) {
          l.dataSource.show = false;
        }
      });
      syncVisibilityForPage(pageId);
    } finally {
      booting.value = false;
    }
  }

  async function toggleLayer(layer) {
    const v = viewer.value;
    if (!v) return;
    const idx = layers.value.findIndex((l) => l.key === layer.key);

    if (!layer.loaded) {
      if (idx >= 0) layers.value[idx].loading = true;
      else {
        const def = defMap[layer.key];
        layers.value.push({
          key: layer.key,
          name: layer.name,
          group: layer.group,
          colorHex: layer.colorHex,
          visible: false,
          loading: true,
          dataSource: null,
        });
      }
      try {
        const item = await loadLayerByKey(v, layer.key);
        const i = layers.value.findIndex((l) => l.key === layer.key);
        const row = {
          ...item,
          loading: false,
          visible: true,
          dataVersion: LAYER_DATA_VERSION,
        };
        if (i >= 0) layers.value[i] = row;
        else layers.value.push(row);
        syncVisibilityForPage(activePage.value);
      } catch (e) {
        console.warn(e);
        const i = layers.value.findIndex((l) => l.key === layer.key);
        if (i >= 0) layers.value.splice(i, 1);
      }
      return;
    }

    const cur = layers.value[idx];
    if (!cur?.dataSource) return;
    cur.visible = !cur.visible;
    syncVisibilityForPage(activePage.value);
  }

  function alertMatchKeys(item) {
    const keys = new Set();
    for (const k of [
      item?.id,
      item?.siteCode,
      item?.raw?.id,
      item?.raw?.siteCode,
      item?.raw?.pointId,
    ]) {
      if (k == null || k === "") continue;
      keys.add(String(k));
    }
    return keys;
  }

  /** 与地图点选一致：从实体属性组装弹窗字段 */
  function fieldsFromEntity(entity) {
    const v = viewer.value;
    if (!v || !entity) return null;
    const time = v.clock.currentTime;
    const fields = {
      名称: entity.name || "未命名要素",
    };
    const ds = entity.entityCollection?.owner;
    if (ds?.name) fields.图层 = ds.name;

    const props = entity.properties;
    if (props) {
      const keys = props.propertyNames || [];
      for (const key of keys) {
        if (
          key === "名称" ||
          key === "name" ||
          key === "fillColor" ||
          key === "borderColor"
        ) {
          continue;
        }
        try {
          let val = props[key]?.getValue?.(time);
          if (val == null || val === "") continue;
          if (typeof val === "object") {
            if (val instanceof Cesium.Cartesian3) continue;
            try {
              val = JSON.stringify(val);
            } catch (_) {
              continue;
            }
          }
          const s = String(val).trim();
          if (s && s !== "null" && s !== "undefined") {
            fields[key] = s;
          }
        } catch (_) {
          /* ignore */
        }
      }
    }
    return fields;
  }

  /** 优先匹配已加载图层上的实体，保证与地图图标位置一致 */
  function findAlertEntity(item) {
    const v = viewer.value;
    const keys = alertMatchKeys(item);
    if (!v || keys.size === 0) return null;

    const preferKey = item?.layerKey;
    const ordered = preferKey
      ? [
          ...layers.value.filter((l) => l.key === preferKey),
          ...layers.value.filter((l) => l.key !== preferKey),
        ]
      : layers.value;

    const time = v.clock.currentTime;
    for (const layer of ordered) {
      const entities = layer.dataSource?.entities?.values;
      if (!entities?.length) continue;

      for (const entity of entities) {
        const code = entity.properties?.编码?.getValue?.(time);
        if (code != null && keys.has(String(code))) return entity;

        const id = entity.properties?.id?.getValue?.(time);
        if (id != null && keys.has(String(id))) return entity;
      }
    }
    return null;
  }

  function entityMapDegrees(entity) {
    const v = viewer.value;
    if (!v || !entity) return null;

    const time = v.clock.currentTime;
    let cartesian = null;
    try {
      cartesian = entity.position?.getValue?.(time) || null;
    } catch (_) {
      cartesian = null;
    }
    if (!cartesian) return null;

    const carto = Cesium.Cartographic.fromCartesian(cartesian);
    return {
      lng: Cesium.Math.toDegrees(carto.longitude),
      lat: Cesium.Math.toDegrees(carto.latitude),
      cartesian,
    };
  }

  function flyToAlert(item) {
    const v = viewer.value;
    if (!v) return;

    const entity = findAlertEntity(item);
    const fromEntity = entityMapDegrees(entity);
    if (fromEntity) {
      flyToLonLat(v, fromEntity.lng, fromEntity.lat, 2200);
      return;
    }

    const w = itemToMapDegrees(item);
    if (!w) return;
    flyToLonLat(v, w.lng, w.lat, 2200);
  }

  /** 列表定位后打开气泡弹窗；第二参数可传告警项或原始 "lng,lat" 字符串 */
  function openPopup(fields, itemOrLonLat) {
    const item =
      typeof itemOrLonLat === "string"
        ? { longitude: itemOrLonLat }
        : itemOrLonLat || {};

    const entity = findAlertEntity(item);
    const fromEntity = entityMapDegrees(entity);
    let cartesian = fromEntity?.cartesian || null;

    if (!cartesian) {
      const w = itemToMapDegrees(item);
      if (w) {
        cartesian = Cesium.Cartesian3.fromDegrees(w.lng, w.lat);
      }
    }

    picked.value = {
      fields: { ...fields },
      cartesian: cartesian ? markRaw(cartesian) : null,
    };
  }

  /** 列表点击：弹窗字段与地图点选实体保持一致 */
  function openAlertPopup(item) {
    const entity = findAlertEntity(item);
    const fromEntity = entityMapDegrees(entity);
    const entityFields = fieldsFromEntity(entity);

    let cartesian = fromEntity?.cartesian || null;
    if (!cartesian) {
      const w = itemToMapDegrees(item);
      if (w) cartesian = Cesium.Cartesian3.fromDegrees(w.lng, w.lat);
    }

    if (entityFields) {
      picked.value = {
        fields: entityFields,
        cartesian: cartesian ? markRaw(cartesian) : null,
      };
      return;
    }

    // 未匹配到地图实体时，退回列表摘要
    openPopup(
      {
        名称: item?.title || item?.raw?.siteName || "站点",
        说明: item?.meta,
        状态: item?.levelLabel,
      },
      item
    );
  }

  function destroy({ destroyViewer = true } = {}) {
    pickHandler?.destroy?.();
    pickHandler = null;
    removePolderLod?.();
    removePolderLod = null;
    if (!destroyViewer) return;
    destroySharedViewer();
    viewer.value = null;
    layers.value = [];
  }

  const store = reactive({
    viewer,
    layers,
    picked,
    hoverTip,
    activePage,
    booting,
    pageLayerItems,
    pageDefs,
    setViewer,
    ensureLayers,
    activatePage,
    toggleLayer,
    syncVisibilityForPage,
    flyToAlert,
    openPopup,
    openAlertPopup,
    destroy,
    loadApiJson,
  });

  return store;
}

export function provideMapStore(store) {
  provide(MAP_KEY, store);
}

export function useMapStore() {
  const store = inject(MAP_KEY);
  if (!store) throw new Error("useMapStore() 需在 Map Provider 内使用");
  return store;
}

/** 拉取监测 JSON（首页 KPI / 告警） */
export async function fetchMonitorList(file) {
  const json = await loadApiJson(file);
  return Array.isArray(json?.data) ? json.data : json?.data?.list || [];
}
